import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { ModalService } from '../../shared/modules/modal';
import {
  ClosableModalWithErrorResponseModel
} from '../../shared/components/base/closable-modal-with-error-response-model';

import {
  CurrencyConversionService,
  SessionService,
  NumberFormatService,
  NotificationService
} from '../../shared/services';

import {
  Entity,
  Country,
  CurrencyScale,
  BaseCurrencyConversion,
  CurrencyConversionPayload,
  CurrencyConversionErrorResponse,
  Currency
} from '../../shared/models';
import { TranslationConstants } from '../../shared/constants';

@Component({
  selector: 'entity-currency-conversion-modal',
  templateUrl: './entity-currency-conversion-modal.component.html',
})
export class EntityCurrencyConversionModalComponent
  extends ClosableModalWithErrorResponseModel<CurrencyConversionErrorResponse>
  implements OnInit {

  data: number; // entity ID
  entities: Entity[];
  selectedEntity: Entity;
  entityCurrency: string;
  taxYear: number;
  taxYearConversions: BaseCurrencyConversion[] = [];
  valuesChanged = false;
  inputsOldValue = {
    0: '',
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
  };
  // flags for minus error display
  inputsContainMinus = [false, false, false, false, false, false];

  scaleEnum = [];
  localCurrencyScale: string;
  displayEntityError = false;
  displayCurrencyScaleError = false;
  displayCurrentTaxYearConversionError = false;
  upeCurrency: Currency;
  private countries: Country[];

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _conversionsService: CurrencyConversionService,
    private _sessionService: SessionService,
    private _numberFormatService: NumberFormatService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new CurrencyConversionErrorResponse();
    this.scaleEnum = Object.values(CurrencyScale).filter(key => !isNaN(Number(CurrencyScale[key])));
    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
  }

  ngOnInit(): void {
    this.getCountries();
    this.getAvailableEntitiesAndMapData();
    this.listenForErrorMessages();
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;
    this.responseErrors = [];
    this.validationErrors = new CurrencyConversionErrorResponse();
    this.displayEntityError = false;
    this.displayCurrencyScaleError = false;

    const payload = new CurrencyConversionPayload();
    payload.conversions = this.taxYearConversions.filter(conversion => conversion.conversionValue);

    if (!this.data) {
      payload.localCurrencyScale = +CurrencyScale[this.localCurrencyScale];
      this.addNewCurrencyConversion(payload);
    } else {
      this.updateCurrencyConversion(payload);
    }
  }

  getCurrencyFromCountries(): void {
    if (this.selectedEntity) {
      this.displayEntityError = false;
      const domicile = this.countries.find((country: Country) => {
        return country.countryId === this.selectedEntity.domicile.countryId;
      });
      this.entityCurrency = domicile.defaultCurrency.isoCode;
    } else {
      this.entityCurrency = '';
    }
  }

  getFormated(num, maxFractionDigits = 1): string {
    if (num === '.') {
      return '0.';
    }
    if (!num) {
      return '';
    }

    return this._numberFormatService.getFormatedNumber(num, maxFractionDigits);
  }

  formatNumberInput(event, element, field, maxFractionDigits = 2, data): void {
    this.valuesChanged = true;

    const validInputRegex = /^[-]?[0-9,]*[.]?[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',') && event.target.value !== '.00';
    const startsWithMinus = firstChar === '-';

    this.inputsContainMinus[field] = event.target.value.includes('-');

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputsOldValue[field];

      return;
    }

    data.conversionValue = this._numberFormatService.handleCursorPosition(
      event,
      element,
      data.value,
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinus
    );
    this.checkConversionRateError(data);
  }

  setOldValue(event, field) {
    this.inputsOldValue[field] = event.target.value;
  }

  checkConversionRateError(conversion: BaseCurrencyConversion): void {
    if (conversion.taxYear !== this.taxYear) {
      return;
    }
    this.displayCurrentTaxYearConversionError = !conversion.conversionValue;
  }

  hideCurrencyScaleError(): void {
    this.displayCurrencyScaleError = false;
  }

  getCurrencyIsoAndName(): string {
    return ` - (${this.upeCurrency.isoCode}) ${this.upeCurrency.name}`;
  }

  // @override
  protected handleErrors({ error, status }: HttpErrorResponse): void {
    this.isSaveDisabled = false;

    if (status === 400) {
      // this.responseErrors.push(error.errors.global);
    } else if (status === 422) {
      this.validationErrors = error.errors;
      const transactionIdsErrors = Object.keys(error.errors).filter((key) => {
        return key.includes('conversions');
      });
      if (transactionIdsErrors.length > 0) {
        this.responseErrors.push(TranslationConstants.ERROR_CONVERSIONS_VALUES);
      }
    } else {
      // this.responseErrors.push(TranslationConstants.GLOBAL_SERVER_ERROR);
    }
  }

  private setTaxYearConversions(data?: BaseCurrencyConversion[]): void {
    const workingContainer = this._sessionService.getWorkingContainer();
    this.taxYear = workingContainer.taxYear;
    const taxYears = [];
    for (let index = this.taxYear; index > this.taxYear - 6; index--) {
      taxYears.push(index);
    }
    this.taxYearConversions = taxYears.map(year => {
      const conversion = new BaseCurrencyConversion();
      conversion.taxYear = year;
      if (data) {
        const existingConversion = data.find(searchData => searchData.taxYear === year);
        if (existingConversion && existingConversion.conversionValue) {
          conversion.conversionValue = existingConversion.conversionValue;
        }
      }
      return conversion;
    });
  }

  private addNewCurrencyConversion(payload: CurrencyConversionPayload): void {
    if (!this.selectedEntity) {
      this.displayEntityError = true;
      if (!this.localCurrencyScale) {
        this.displayCurrencyScaleError = true;
      }
      this.isSaveDisabled = false;
      return;
    }

    this._conversionsService.saveCurrencyConversion(this.selectedEntity.entityId, payload).subscribe(() => {
      this.closeAllModals();
      this.success();
    });
  }

  private updateCurrencyConversion(payload: CurrencyConversionPayload): void {
    this._conversionsService.updateCurrencyConversion(this.data, payload).subscribe(() => {
      this.closeAllModals();
      this.success();
    });
  }

  private getAvailableEntitiesAndMapData(): void {
    this._conversionsService.getAvailableEntities().subscribe(({ data }) => {
      this.entities = data;
      if (this.data) {
        this.getCurrencyConversion();
      } else {
        this.setTaxYearConversions();
      }
    });
  }

  private getCurrencyConversion(): void {
    this._conversionsService.show(this.data).subscribe(({ data }) => {
      this.selectedEntity = data[0].legalEntity;
      this.localCurrencyScale = CurrencyScale[data[0].localCurrencyScale];
      this.setTaxYearConversions(data);
      this.getCurrencyFromCountries();
    });
  }

  private getCountries(): void {
    this.countries = this._sessionService.getCountries();
  }
}
